import { useEffect, useState } from 'react'
import { toast } from 'sonner'

import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { PersonForm, type PersonFormValues } from './person-form'
import { useCreatePerson } from '@/api/hooks'
import type { CreatePersonInput, TPerson } from '@schemas'

export interface PersonCreateSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: (person: TPerson, options?: { entrantLinked: boolean }) => void
}

export function PersonCreateSheet({ open, onOpenChange, onCreated }: PersonCreateSheetProps) {
  const createPerson = useCreatePerson()
  const [formKey, setFormKey] = useState(0)

  const createDisabled = createPerson.isPending

  useEffect(() => {
    if (!open) {
      setFormKey(prev => prev + 1)
    }
  }, [open])

  const handleClose = () => {
    if (createDisabled) return
    onOpenChange(false)
  }

  const handleSubmit = async (values: PersonFormValues) => {
    const normalize = (value?: string) => {
      const trimmed = value?.trim()
      return trimmed && trimmed.length > 0 ? trimmed : undefined
    }
    const normalizeState = (value?: string) => {
      const trimmed = normalize(value)
      return trimmed ? trimmed.toUpperCase() : undefined
    }

    const fullName = `${values.firstName} ${values.lastName}`.replace(/\s+/g, ' ').trim()
    const payload: CreatePersonInput = {
      name: fullName,
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      ahstcMember: values.ahstcMember,
    }

    if (values.ahstcMember && values.ahstcJudge) {
      payload.ahstcJudge = true
    }

    const email = normalize(values.email)
    if (email) payload.email = email
    const phone = normalize(values.phone)
    if (phone) payload.phone = phone
    const notes = normalize(values.notes)
    if (notes) payload.notes = notes

    const displayCity = normalize(values.displayCity)
    if (displayCity) payload.displayCity = displayCity
    const displayState = normalizeState(values.displayState)
    if (displayState) payload.displayState = displayState

    const mailingAddress = normalize(values.mailingAddress)
    if (mailingAddress) payload.mailingAddress = mailingAddress
    const mailingCity = normalize(values.mailingCity)
    if (mailingCity) payload.mailingCity = mailingCity
    const mailingState = normalizeState(values.mailingState)
    if (mailingState) payload.mailingState = mailingState
    const mailingZip = normalize(values.mailingZipCode)
    if (mailingZip) payload.mailingZipCode = mailingZip

    try {
      const person = await createPerson.mutateAsync(payload)
      toast.success(`Added ${person.name ?? 'person'}`)
      setFormKey(prev => prev + 1)
      onOpenChange(false)
      onCreated?.(person, { entrantLinked: false })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to add person'
      toast.error(message)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl">
        <SheetHeader className="space-y-1">
          <SheetTitle>Add person</SheetTitle>
          <SheetDescription>
            Capture contact details and membership status. A person is shared across every show.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 h-[calc(100vh-12rem)]">
          <PersonForm
            key={formKey}
            onSubmit={handleSubmit}
            disabled={createDisabled}
            defaultValues={{ ahstcMember: false }}
          >
            <SheetFooter className="flex w-full flex-col gap-3">
              <p className="text-xs text-muted-foreground">
                To enter this person in a show, register them from that show's Run mode intake
                workspace after saving.
              </p>

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={createDisabled}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={createDisabled}>
                  {createDisabled ? 'Saving…' : 'Add person'}
                </Button>
              </div>
            </SheetFooter>
          </PersonForm>
        </div>
      </SheetContent>
    </Sheet>
  )
}
